import { useCallback, useContext, useEffect } from 'react';
import { CanvasElementType } from '../canvas-types';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import { SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { HighlightEntityContext } from './HighlightEntityContext';
import { ObjectOperations } from '../node-changes-event-handlers/objectOperations';
import { RFEdgeFloatingEdge } from '../edges';
import {
  NODE_Z_INDEX_BASE,
  NODE_Z_INDEX_HIGHLIGHTED,
  EDGE_Z_INDEX_DEFAULT,
  EDGE_Z_INDEX_HIGHLIGHTED,
} from '../const/zIndex';

const DEFAULT_TIMEOUT = 3000;

const selectHighlightedRegularOrGroupLabelNodes = (node: SweepCanvasRfNode) => {
  return (
    (node.type === CanvasElementType.REGULAR ||
      node.type === CanvasElementType.GROUP_LABEL ||
      node.type === CanvasElementType.STANDALONE_NODE) &&
    Boolean(node.data?.isHighlighted)
  );
};

const selectHighlightedEdge = (edge: RFEdgeFloatingEdge) => {
  return Boolean(edge.data?.highlightType);
};

export const clearNodeOperationsHighlight = (
  nodeOperations: ObjectOperations<SweepCanvasRfNode>,
) => {
  nodeOperations.setObjectsBySelector(selectHighlightedRegularOrGroupLabelNodes, (node) => {
    return {
      ...node,
      data: {
        ...node.data,
        isHighlighted: false,
      },
      zIndex: NODE_Z_INDEX_BASE,
    } as SweepCanvasRfNode;
  });
  return nodeOperations;
};

export const clearEdgeOperationsHighlight = (
  edgeOperations: ObjectOperations<RFEdgeFloatingEdge>,
) => {
  edgeOperations.setObjectsBySelector(selectHighlightedEdge, (edge) => {
    return {
      ...edge,
      data: {
        ...edge.data,
        highlightType: null,
      },
      zIndex: EDGE_Z_INDEX_DEFAULT,
    } as RFEdgeFloatingEdge;
  });
  return edgeOperations;
};
export const useHighlightNodesAndEdges = (disableHighlight?: boolean) => {
  const { setCanvasNodes, setCanvasEdges, getCanvasEdges } = useSweepCanvasState();

  const { timeoutRef } = useContext(HighlightEntityContext);

  const clearHighlight = useCallback(() => {
    if (disableHighlight) return;
    clearTimeout(timeoutRef.current?.timeout);
    timeoutRef.current = null;

    setCanvasNodes((_, nodeOperations) =>
      clearNodeOperationsHighlight(nodeOperations).getObjects(),
    );
    setCanvasEdges((_, edgeOperations) =>
      clearEdgeOperationsHighlight(edgeOperations).getObjects(),
    );
  }, [disableHighlight, setCanvasEdges, setCanvasNodes, timeoutRef]);

  const startHighlightNodeAndConnectedEdges = useCallback(
    (nodeId: string) => {
      if (disableHighlight) return;
      // Clear any existing timeout
      clearTimeout(timeoutRef.current?.timeout);
      if (timeoutRef.current?.entityId === nodeId) {
        timeoutRef.current.timeout = setTimeout(clearHighlight, DEFAULT_TIMEOUT);
      } else {
        timeoutRef.current = {
          timeout: setTimeout(clearHighlight, DEFAULT_TIMEOUT),
          entityId: nodeId,
        };

        setCanvasNodes((prevNodes) =>
          prevNodes.map((node) => {
            const isHighlighted = node.id === nodeId;

            if (
              node.type === CanvasElementType.GROUP_LABEL ||
              node.type === CanvasElementType.STANDALONE_NODE ||
              (node.type === CanvasElementType.REGULAR &&
                Boolean(node.data?.isHighlighted) !== isHighlighted)
            ) {
              return {
                ...node,
                data: {
                  ...node.data,
                  isHighlighted,
                },
                zIndex: isHighlighted ? NODE_Z_INDEX_HIGHLIGHTED : NODE_Z_INDEX_BASE,
              } as SweepCanvasRfNode;
            }

            return node;
          }),
        );

        setCanvasEdges((prevEdges) =>
          prevEdges.map((edge) => {
            if (edge.data) {
              if (edge.source === nodeId) {
                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    highlightType: 'source',
                  },
                  zIndex: EDGE_Z_INDEX_HIGHLIGHTED,
                };
              } else if (edge.target === nodeId) {
                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    highlightType: 'target',
                  },
                  zIndex: EDGE_Z_INDEX_HIGHLIGHTED,
                };
              } else if (edge.data.highlightType) {
                return {
                  ...edge,
                  data: {
                    ...edge.data,
                    highlightType: null,
                  },
                  zIndex: EDGE_Z_INDEX_DEFAULT,
                };
              }
            }
            return edge;
          }),
        );
      }
    },
    [clearHighlight, disableHighlight, setCanvasEdges, setCanvasNodes, timeoutRef],
  );

  const startHighlightEdge = useCallback(
    (edgeId: string) => {
      if (disableHighlight) return;
      clearTimeout(timeoutRef.current?.timeout);
      if (timeoutRef.current?.entityId === edgeId) {
        timeoutRef.current.timeout = setTimeout(clearHighlight, DEFAULT_TIMEOUT);
      } else {
        timeoutRef.current = {
          timeout: setTimeout(clearHighlight, DEFAULT_TIMEOUT),
          entityId: edgeId,
        };

        const edge = getCanvasEdges().find((edge) => edge.id === edgeId);
        if (edge?.data?.highlightType) {
          return;
        }

        setCanvasNodes((prevNodes) =>
          prevNodes.map((node) => {
            if (
              node.type === CanvasElementType.GROUP_LABEL ||
              node.type === CanvasElementType.REGULAR
            ) {
              if (node.data.isHighlighted) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    isHighlighted: false,
                  },
                } as SweepCanvasRfNode;
              }
            }
            return node;
          }),
        );

        setCanvasEdges((prevEdges) =>
          prevEdges.map((edge) => {
            const isHighlighted = edge.id === edgeId;
            if (edge.data) {
              if (isHighlighted && !edge.data.highlightType) {
                return {
                  ...edge,
                  data: { ...edge.data, highlightType: 'simple' },
                  zIndex: EDGE_Z_INDEX_HIGHLIGHTED,
                };
              } else {
                if (edge.data.highlightType) {
                  return {
                    ...edge,
                    data: { ...edge.data, highlightType: null },
                    zIndex: EDGE_Z_INDEX_DEFAULT,
                  };
                }
              }
            }
            return edge;
          }),
        );
      }
    },
    [clearHighlight, disableHighlight, getCanvasEdges, setCanvasEdges, setCanvasNodes, timeoutRef],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current?.timeout) {
        clearTimeout(timeoutRef.current.timeout);
      }
    };
  }, [timeoutRef]);

  return { startHighlightNodeAndConnectedEdges, startHighlightEdge, clearHighlight };
};
